// buscador-wizard.component.ts
import { Component, OnInit } from "@angular/core";
import { CommonModule } from "@angular/common";
import {
  FormBuilder,
  FormGroup,
  Validators,
  ReactiveFormsModule,
  FormArray,
} from "@angular/forms";
import { Router } from "@angular/router";
import { EspecialistaService } from "src/app/core/services/especialista.service";
import { CitaService } from "src/app/core/services/cita.service";
import { Especialista } from "src/app/core/models";
import { PacientesService } from "src/app/core/services/paciente.service";
import { ConvenioService } from "src/app/core/services/convenio.service";
import {
  NgbModal,
  NgbModalRef,
  NgbModule,
  NgbNavModule,
} from "@ng-bootstrap/ng-bootstrap";

import { CalendarioComponent } from "../citas/calendario/calendario.component";

import { ExpandableTextComponent } from "../citas/citas-list/expandible.component";

interface StepOption {
  id: string;
  text: string;
  value: string;
}

@Component({
  selector: "app-buscador-wizard",
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule, ExpandableTextComponent],
  templateUrl: "./buscador-wizard.component.html",
  styleUrl: "./buscador-wizard.componentv2.scss",
})
export class BuscadorWizardComponent implements OnInit {
  currentStep = 0;
  wizardForm: FormGroup;

  // Datos del paciente (asumimos que vienen de otro lado)
  patient = {
    name: "Juan Pérez",
    age: 25,
  };

  // Definición de los pasos
  steps = [
    {
      title: "Modalidad de Atención",
      question: "¿Qué modalidad de atención prefieres?",
      options: [
        { id: "virtual", text: "Virtual", value: "Virtual"},
        {
          id: "presencial_norte",
          ciudadId: 149237,
          text: "Presencial Norte Bogotá",
          value: "Presencial_Norte",
        },
        {
          id: "presencial_country",
          ciudadId: 149237,
          text: "Presencial Country Bogotá",
          value: "Presencial_Country",
        },
        {
          id: "presencial_medellin",
          ciudadId: 21074,
          text: "Presencial Medellín",
          value: "Presencial_Medellin",
        },
      ],
      completed: false,
    },
    {
      title: "Rango de Edad",
      question: "¿Cuál es tu rango de edad?",
      options: [
        { id: "age_4_12", text: "4-12 años", value: "4-12" },
        { id: "age_13_18", text: "13-18 años", value: "13-18" },
        { id: "age_19_25", text: "19-25 años", value: "19-25" },
        { id: "age_26_35", text: "26-35 años", value: "26-35" },
        { id: "age_36_50", text: "36-50 años", value: "36-50" },
        { id: "age_51_plus", text: "51 en adelante", value: "51+" },
      ],
      completed: false,
    },
    {
      title: "Tipo de Atención",
      question: "¿Qué tipo de atención requieres?",
      options: [
        { id: "individual", text: "Terapia Individual", value: "individual" },
        { id: "pareja", text: "Terapia de Pareja", value: "pareja" },
        { id: "familiar", text: "Terapia Familiar", value: "familiar" },
        {
          id: "neuropsicologia",
          text: "Neuropsicología",
          value: "neuropsicologia",
        },
        { id: "psiquiatria", text: "Psiquiatria", value: "psiquiatria" },
      ],
      completed: false,
    },
    {
      title: "Género del Terapeuta",
      question: "¿Qué género prefieres que sea tu terapeuta?",
      options: [
        { id: "mujer", text: "Mujer", value: "F" },
        { id: "hombre", text: "Hombre", value: "M" },
        { id: "indiferente", text: "Indiferente", value: "indiferente" },
      ],
      completed: false,
    },
    {
      title: "Motivo de Consulta",
      question: "¿Por cuál de los siguientes motivos consultas?",
      options: [], // Se llenará dinámicamente según la edad
      completed: false,
      isMultiple: true,
    },
  ];

  // Motivos de consulta para adultos
  adultMotives: StepOption[] = [
    { id: "ansiedad", text: "Ansiedad", value: "ansiedad" },
    { id: "depresion", text: "Depresión", value: "depresion" },
    { id: "duelo", text: "Duelo", value: "duelo" },
    { id: "pareja", text: "Problemas de pareja", value: "pareja" },
    {
      id: "suicida",
      text: "Ideación de muerte / conducta suicida",
      value: "suicida",
    },
    {
      id: "alimentaria",
      text: "Problemas de conducta alimentaria",
      value: "alimentaria",
    },
    { id: "estres", text: "Manejo de estrés", value: "estres" },
    {
      id: "timidez",
      text: "Timidez / dificultades para hablar en público",
      value: "timidez",
    },
    { id: "ira", text: "Manejo de la ira", value: "ira" },
    {
      id: "dolor",
      text: "Manejo de dolor / enfermedades crónicas",
      value: "dolor",
    },
    { id: "fobias", text: "Fobias", value: "fobias" },
    { id: "trauma", text: "Trauma", value: "trauma" },

    {
      id: "aprendizaje",
      text: "Dificultades de aprendizaje",
      value: "aprendizaje",
    },
    { id: "autoestima", text: "Problemas de autoestima", value: "autoestima" },
    { id: "violencia", text: "Violencia intrafamiliar", value: "violencia" },
    { id: "familiar", text: "Problemáticas familiares", value: "familiar" },
    { id: "atencion", text: "Trastornos atencionales", value: "atencion" },
    { id: "memoria", text: "Problemas de memoria", value: "memoria" },
    {
      id: "ejecutivas",
      text: "Dificultades en funciones ejecutivas",
      value: "ejecutivas",
    },
    {
      id: "laboral",
      text: "Problemáticas en el entorno laboral",
      value: "laboral",
    },
    { id: "bullyng", text: "Bullyng", value: "bullyng" },
    { id: "autolesion", text: "Autolesión", value: "autolesion" },
    { id: "escolares", text: "Problemáticas escolares", value: "escolares" },

    // { id: 'otros', text: 'Hablar con un asesor', value: 'otros' }
  ];

  // Motivos de consulta para niños y adolescentes
  childMotives: StepOption[] = [
    {
      id: "instrucciones",
      text: "Problemas de seguimiento de instrucciones o normas",
      value: "instrucciones",
    },
    { id: "emocional", text: "Gestión emocional", value: "emocional" },
    { id: "bullying", text: "Bullying", value: "bullying" },
    {
      id: "ansiedad_depresion",
      text: "Ansiedad y depresión",
      value: "ansiedad_depresion",
    },
    { id: "autolesion", text: "Autolesión", value: "autolesion" },
    { id: "escolares", text: "Problemáticas escolares", value: "escolares" },
    { id: "separacion", text: "Separación padres", value: "separacion" },
    {
      id: "alimentacion",
      text: "Problemas en alimentación",
      value: "alimentacion",
    },
    { id: "fobias", text: "Fobias", value: "fobias" },
    { id: "relaciones", text: "Problemas en relaciones", value: "relaciones" },
    {
      id: "timidez",
      text: "Timidez / dificultades para hablar en público",
      value: "timidez",
    },
    { id: "duelo", text: "Duelo", value: "duelo" },
    { id: "violencia", text: "Violencia intrafamiliar", value: "violencia" },
    {
      id: "autoestima",
      text: "Dificultades en autoestima",
      value: "autoestima",
    },
    { id: "otros", text: "Otros: Hablar con un asesor", value: "otros" },
  ];

  showResult = false; // Variable para controlar qué mostrar
  spinner: Boolean = true;

  pacienteAntiguo: boolean = false;

  idPaciente: string = "";

  especialistas: Especialista[] = [] as Especialista[];

  especialistasFiltrados: any[] = [];

  datosPaciente: any;

  convenios = [];

  constructor(
    private fb: FormBuilder,
    private router: Router,
    private __especialistasService: EspecialistaService,
    private __citasservice: CitaService,
    private __pacientesService: PacientesService,
    private __convenioService: ConvenioService,
    private modalService: NgbModal
  ) {
    this.createForms();
    this.setMotivesBasedOnAge();
  }

  ngOnInit() {
    // Inicializaciones adicionales si son necesarias
    const currentUser = localStorage.getItem("currentPatient");
    if (currentUser) {
      this.idPaciente = JSON.parse(currentUser).userId;
      this.getPaciente(this.idPaciente);
    }
  }

  private setMotivesBasedOnAge() {
    this.steps[4].options =
      this.patient.age >= 18 ? this.adultMotives : this.childMotives;
  }

  createForms() {
    const formGroup: any = {};
    this.steps.forEach((step, index) => {
      if (step.isMultiple) {
        formGroup[`step${index}`] = this.fb.array([]);
      } else {
        formGroup[`step${index}`] = ["", Validators.required];
      }
    });
    this.wizardForm = this.fb.group(formGroup);

    // Suscribirse a cambios en el formulario
    this.wizardForm.valueChanges.subscribe(() => {
      this.updateStepCompletion();
    });
  }

  updateStepCompletion() {
    this.steps.forEach((step, index) => {
      const control = this.wizardForm.get(`step${index}`);
      if (control) {
        step.completed = step.isMultiple
          ? control.value.length > 0
          : control.valid && control.value !== "";
      }
    });
  }

  nextStep() {
    if (this.currentStep < this.steps.length - 1) {
      if (this.canProceedToNextStep()) {
        this.currentStep++;
      }
    } else {
      this.submitWizard();
    }
  }

  previousStep() {
    if (this.currentStep > 0) {
      this.currentStep--;
    }
  }

  canProceedToNextStep(): boolean {
    return this.steps[this.currentStep].completed;
  }

  goToStep(index: number) {
    if (this.canGoToStep(index)) {
      this.currentStep = index;
    }
  }

  canGoToStep(stepIndex: number): boolean {
    for (let i = 0; i < stepIndex; i++) {
      if (!this.steps[i].completed) {
        return false;
      }
    }
    return true;
  }

  isStepCompleted(index: number): boolean {
    return this.steps[index].completed;
  }

  isLastStep(): boolean {
    return this.currentStep === this.steps.length - 1;
  }

  submitWizard() {
    if (this.isLastStep() && this.steps.every((step) => step.completed)) {

      // Traer la modalidad seleccionada
      const modalidadValue = this.wizardForm.value.step0;

      // Traer el objeto completo de la modalidad seleccionada
      const selectedOption = this.steps[0].options.find(
        (opt) => opt.value === modalidadValue
      );
      
      //Convertir ciudadId en número
      const ciudadId = selectedOption?.ciudadId
        ? Number(selectedOption.ciudadId)
        : null;

      // Agregar la ciudadId, a la respuesta
      const answers = this.wizardForm.value;
      answers.ciudadId = ciudadId;

      const formData = {
        patient: this.patient,
        answers: this.wizardForm.value,
        ciudadId: ciudadId,
      };
      console.log("Formulario completo:", formData);
      // Aquí iría la lógica para procesar las respuestas y recomendar especialistas
      /** 
       * {
            "step0": "presencial_medellin",
            "step1": "51+",
            "step2": "psiquiatria",
            "step3": "indiferente",
            "step4": [
                "escolares"
            ]
        }
       */

      // Mostrar el resultado
      this.showResult = true;

      this.resultEspecialistas(formData.answers);
      // this.router.navigate(["/citas-list"]);
    }
  }
  // Agregar este método en el componente
  onCheckboxChange(event: any, stepIndex: number) {
    const formArray = this.wizardForm.get(`step${stepIndex}`) as FormArray;
    const value = event.target.value;

    if (event.target.checked) {
      formArray.push(this.fb.control(value));
    } else {
      const index = formArray.controls.findIndex(
        (control) => control.value === value
      );
      if (index >= 0) {
        formArray.removeAt(index);
      }
    }
  }

  async getPaciente(idPaciente: string) {
    this.__pacientesService.getDocument(idPaciente).then((res: any) => {
      this.datosPaciente = res;
      if (this.datosPaciente.idEmpresa) {
        this.__convenioService
          .getConveniosEmpresaActive(this.datosPaciente.idEmpresa.$id)
          .then((convenios: any) => {
            this.convenios = convenios;
          })
          .catch((error) => {
            console.log(
              "error al consultar informacion de los convenios",
              error
            );
          });
      }
    });

    // VALIDAR SI ES PACIENTE NUEVO O NO Para luego filtrar los especialistas por el atributo soloPacientesAntiguos
    await this.__citasservice.getLastCita(idPaciente).then((res: any) => {
      this.pacienteAntiguo = res ? true : false;
    });
    console.log("PACIENTE ANTIGUO: ", this.pacienteAntiguo);
  }

  // Listado de especialistas.
  async resultEspecialistas(formData: any) {
    this.__especialistasService
      .getEspecialistasEncuentra(this.pacienteAntiguo, formData)
      .then((response: Especialista[]) => {
        this.especialistas = response;
        this.especialistasFiltrados = response;

        this.spinner = false;
        // console.log("responseList", response);
      })
      .catch((error) => {
        console.log(error);
      });
  }

  // Muestra el modal del calendario para seleccionar una hora
  showAgenda(datos: any) {
    // TODO: Revisar todos los datos pasados al modal se envia información sensible
    // console.log("datos enviados al modal:::", datos);
    const modalRef: NgbModalRef = this.modalService.open(CalendarioComponent, {
      size: "md",
      windowClass: "calendario-modal",
    });
    if (this.idPaciente) {
      this.datosPaciente.convenios = this.convenios;
      datos.datosPaciente = this.datosPaciente;
    }
    modalRef.componentInstance.datos = datos; // Pasar datos al componente modal
  }
}
